import { useMemo, useState } from 'react'
import { NoWrapHyphens } from './NoWrapHyphens.js'
import { usePageMeta } from '../hooks/ui/usePageMeta'
import { useAboutContent } from '../hooks/i18n/usePageContent.js'
import { CONTACT_CONFIG } from '../config/constants.js'
import { useTheme } from '../context/ThemeContext.js'
import { LocaleToggle } from './ui/LocaleToggle.js'
import * as TOKENS from './chat/design-tokens'

/* ── Types ─────────────────────────────────────────────────────────── */

interface AboutPageProps {
  onBack: () => void
  onStartChat?: () => void
}

interface LogoAsset {
  name: string
  sourceUrl: string
  href: string
}

/* ── Static data (proper nouns / brand assets) ─────────────────────── */

const LOGO_ASSETS: LogoAsset[] = [
  { name: 'GitHub',     sourceUrl: 'https://github.com/github.png?size=128',     href: 'https://github.com/github' },
  { name: 'Google',     sourceUrl: 'https://github.com/google.png?size=128',     href: 'https://github.com/google' },
  { name: 'OpenAI',     sourceUrl: 'https://github.com/openai.png?size=128',     href: 'https://github.com/openai' },
  { name: 'Anthropic',  sourceUrl: 'https://github.com/anthropics.png?size=128', href: 'https://github.com/anthropics' },
  { name: 'Vercel',     sourceUrl: 'https://github.com/vercel.png?size=128',     href: 'https://github.com/vercel' },
  { name: 'Supabase',   sourceUrl: 'https://github.com/supabase.png?size=128',   href: 'https://github.com/supabase' },
  { name: 'Node.js',    sourceUrl: 'https://github.com/nodejs.png?size=128',     href: 'https://github.com/nodejs' },
  { name: 'React',      sourceUrl: 'https://github.com/facebook.png?size=128',   href: 'https://github.com/facebook' },
  { name: 'TypeScript', sourceUrl: 'https://github.com/microsoft.png?size=128',  href: 'https://github.com/microsoft' },
  { name: 'AWS',        sourceUrl: 'https://github.com/aws.png?size=128',        href: 'https://github.com/aws' },
  { name: 'Redis',      sourceUrl: 'https://github.com/redis.png?size=128',      href: 'https://github.com/redis' },
]

const ABOUT_CANONICAL = 'https://www.farzadbayat.com/about'

/* ── Shared class atoms ─────────────────────────────────────────────── */

/** Section shell — matches landing page sectionShellClass exactly */
const sectionShell = `w-full border-t px-6 py-20 md:px-12 ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.base}`

/** Eyebrow label — same as landing page section eyebrows */
const eyebrow = `${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-[0.25em] ${TOKENS.TEXT.muted}`

/** Section heading — h2 level */
const sectionHeading = `text-3xl font-bold tracking-tight md:text-4xl ${TOKENS.TEXT.primary}`

/** Body paragraph */
const bodyText = `text-lg font-light leading-relaxed ${TOKENS.TEXT.secondary}`

/** Primary CTA — matches landing page exactly */
const primaryCta = TOKENS.PATTERNS.marketingPrimaryLarge

/** Secondary CTA — matches landing page exactly */
const secondaryCta = TOKENS.PATTERNS.marketingSecondaryLarge

/* ── Component ──────────────────────────────────────────────────────── */

function AboutPage({ onBack, onStartChat }: AboutPageProps) {
  const { isDarkMode } = useTheme()
  const content = useAboutContent()
  const [selectedTech, setSelectedTech] = useState<string | null>(null)
  const [expandedProjectId, setExpandedProjectId] = useState<string>(content.aiProjects[0]?.id ?? '')

  const projectsWithStack = useMemo(
    () =>
      content.aiProjects.map(project => ({
        ...project,
        stack: project.stack ?? [],
      })),
    [content.aiProjects],
  )

  const allTech = useMemo(
    () => Array.from(new Set(projectsWithStack.flatMap(p => p.stack))).sort((a, b) => a.localeCompare(b)),
    [projectsWithStack],
  )

  const visibleProjects = useMemo(
    () => (selectedTech ? projectsWithStack.filter(p => p.stack.includes(selectedTech)) : projectsWithStack),
    [selectedTech, projectsWithStack],
  )

  usePageMeta(content.meta.title, content.meta.description, ABOUT_CANONICAL, {
    ogTitle: content.meta.ogTitle,
    ogDesc: content.meta.ogDescription,
    ogUrl: ABOUT_CANONICAL,
    twitterTitle: content.meta.twitterTitle,
    twitterDesc: content.meta.twitterDescription,
  })

  return (
    <div
      className={`relative z-30 h-[100dvh] w-full overflow-y-auto scroll-smooth ${TOKENS.SURFACE_ROLES.base} custom-scrollbar`}
    >
      {/* ── Sticky header ─────────────────────────────────────────── */}
      <header
        className={`sticky top-0 z-40 border-b ${TOKENS.BORDER.default} ${TOKENS.CHAT_SURFACE.header}`}
      >
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4 md:px-12">

          {/* Back */}
          <button
            type="button"
            onClick={onBack}
            className={`
              group relative inline-flex items-center gap-2 overflow-hidden
              border px-4 py-2 min-h-11 ${TOKENS.TEXT_SIZE.sm} font-medium
              rounded-none ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.base}
              transition-colors duration-150 hover:bg-muted
              ${TOKENS.INTERACTION.focusRing}
            `}
          >
            <svg className="h-3.5 w-3.5 transition-transform duration-150 group-hover:-translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {content.nav.back}
          </button>

          {/* Header CTAs */}
          <div className={`flex items-center ${TOKENS.GAP.sm}`}>
            <LocaleToggle className="future-locale-toggle" />
            <button
              type="button"
              onClick={onStartChat}
              className={`
                group relative inline-flex items-center gap-2 overflow-hidden
                rounded-none px-4 py-2 min-h-11 ${TOKENS.TEXT_SIZE.sm} font-medium
                bg-foreground text-background border border-transparent
                transition-colors duration-150 hover:bg-foreground/90
                ${TOKENS.INTERACTION.focusRing}
              `}
            >
              <span className={TOKENS.INTERACTION.shimmer} />
              <span className="relative">{content.nav.startChat}</span>
            </button>
            <button
              type="button"
              onClick={() => window.open(CONTACT_CONFIG.SCHEDULING.BOOKING_URL, '_blank')}
              className={`
                group relative inline-flex items-center gap-2 overflow-hidden
                rounded-none px-4 py-2 min-h-11 ${TOKENS.TEXT_SIZE.sm} font-medium
                border ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.base}
                transition-colors duration-150 hover:bg-muted
                ${TOKENS.INTERACTION.focusRing}
              `}
            >
              <span className={TOKENS.INTERACTION.shimmer} />
              <span className="relative">{content.nav.bookCall}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-col">

        {/* ── Hero / Identity ───────────────────────────────────── */}
        <section className={sectionShell} aria-labelledby="about-heading">
          <div className={`flex max-w-5xl flex-col items-center gap-12 md:flex-row md:items-start`}>
            {/* Text column */}
            <div className={`flex flex-1 flex-col ${TOKENS.GAP.xl}`}>
              <div className={`flex flex-col ${TOKENS.GAP.md}`}>
                <span className={eyebrow}>{content.hero.eyebrow}</span>
                <h1
                  id="about-heading"
                  className="font-display text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl text-foreground"
                >
                  <NoWrapHyphens text={content.hero.titleLine1} />
                  {content.hero.titleLine2 ? (
                    <>
                      <br />
                      {content.hero.titleLine2}
                    </>
                  ) : null}
                </h1>
                <p className={bodyText}>
                  {content.hero.body}
                </p>
              </div>

              {/* Hero CTAs */}
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={onStartChat}
                  className={`group relative w-full overflow-hidden sm:w-auto ${primaryCta}`}
                >
                  <span className={TOKENS.INTERACTION.shimmer} />
                  <span className="relative">{content.hero.talkToNow}</span>
                  <svg
                    className="relative h-4 w-4 transition-transform duration-150 group-hover:translate-x-1"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
                <a
                  href="/resume.pdf"
                  download={content.hero.resumeFilename}
                  className={`group relative w-full overflow-hidden sm:w-auto ${secondaryCta}`}
                >
                  <span className={TOKENS.INTERACTION.shimmer} />
                  <span className="relative">{content.hero.downloadResume}</span>
                  <svg
                    className="relative h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                  </svg>
                </a>
              </div>
            </div>
            {/* End text column */}

            {/* Image column */}
            <div className="flex shrink-0 items-start justify-center md:justify-end">
              <img
                src="/farzad-bayat.jpg"
                alt={content.hero.imageAlt}
                className={`
                  h-56 w-56 object-cover border
                  md:h-72 md:w-72
                  ${TOKENS.BORDER.default}
                `}
              />
            </div>
          </div>
        </section>

        {/* ── AI Projects ───────────────────────────────────────── */}
        <section className={sectionShell} aria-labelledby="projects-heading">
          <div className={`flex flex-col ${TOKENS.GAP['2xl']}`}>

            {/* Header + filter row */}
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className={`flex flex-col ${TOKENS.GAP.md}`}>
                <span className={eyebrow}>{content.projects.eyebrow}</span>
                <h2 id="projects-heading" className={sectionHeading}>
                  {content.projects.title}
                </h2>
              </div>

              {/* Tech filter chips — flat, no rounded-full */}
              <div className="flex flex-wrap gap-2" role="group" aria-label={content.projects.filterAriaLabel}>
                <button
                  type="button"
                  onClick={() => setSelectedTech(null)}
                  className={`
                    rounded-none border px-3 py-1.5 min-h-11 ${TOKENS.TEXT_SIZE.xs} font-mono
                    uppercase tracking-wide transition-colors duration-150
                    ${TOKENS.INTERACTION.focusRing}
                    ${selectedTech === null
                      ? 'border-foreground bg-foreground text-background'
                      : `${TOKENS.BORDER.default} ${TOKENS.TEXT.muted} hover:border-foreground/40 hover:text-foreground`
                    }
                  `}
                >
                  {content.projects.filterAll}
                </button>
                {allTech.map(tech => (
                  <button
                    type="button"
                    key={tech}
                    onClick={() => setSelectedTech(prev => (prev === tech ? null : tech))}
                    className={`
                      rounded-none border px-3 py-1.5 min-h-11 ${TOKENS.TEXT_SIZE.xs} font-mono
                      uppercase tracking-wide transition-colors duration-150
                      ${TOKENS.INTERACTION.focusRing}
                      ${selectedTech === tech
                        ? 'border-[#c95f3a] bg-[#c95f3a]/5 text-[#c95f3a]'
                        : `${TOKENS.BORDER.default} ${TOKENS.TEXT.muted} hover:border-foreground/40 hover:text-foreground`
                      }
                    `}
                  >
                    {tech}
                  </button>
                ))}
              </div>
            </div>

            {/* Project rows — Linear-style bordered list */}
            <div className="flex flex-col border-t border-border" role="list">
              {visibleProjects.map(project => {
                const isExpanded = expandedProjectId === project.id
                return (
                  <article
                    key={project.id}
                    role="listitem"
                    className={`
                      group flex flex-col gap-5 border-b py-8
                      ${TOKENS.BORDER.default}
                      transition-colors duration-150 hover:bg-secondary/40
                    `}
                  >
                    {/* Row header */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className={`flex flex-col ${TOKENS.GAP.xs}`}>
                        <h3 className={`text-xl font-semibold tracking-tight ${TOKENS.TEXT.primary}`}>
                          {project.name}
                        </h3>
                        <p className={`${TOKENS.TEXT_SIZE.xs} font-mono ${TOKENS.TEXT.muted}`}>
                          {project.role} · {project.duration}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedProjectId(isExpanded ? '' : project.id)}
                        className={`
                          group/btn relative inline-flex shrink-0 items-center gap-2 overflow-hidden
                          self-start rounded-none border px-4 py-2
                          ${TOKENS.TEXT_SIZE.xs} font-medium tracking-wide
                          ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.base}
                          transition-colors duration-150 hover:bg-muted
                          ${TOKENS.INTERACTION.focusRing}
                        `}
                        aria-expanded={isExpanded}
                      >
                        <span className={TOKENS.INTERACTION.shimmer} />
                        <span className="relative">
                          {isExpanded ? content.projects.collapse : content.projects.expand}
                        </span>
                        <svg
                          className={`relative h-3 w-3 transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    </div>

                    <p className={`max-w-2xl text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
                      {project.summary}
                    </p>

                    {/* Stack chips */}
                    <div className="flex flex-wrap gap-2">
                      {project.stack.map(tech => (
                        <span
                          key={`${project.id}-${tech}`}
                          className={`
                            ${TOKENS.TEXT_SIZE.xs} font-mono px-2.5 py-1 border rounded-none
                            ${selectedTech === tech
                              ? 'border-[#c95f3a]/50 bg-[#c95f3a]/5 text-[#c95f3a]'
                              : `${TOKENS.BORDER.default} bg-secondary ${TOKENS.TEXT.muted}`
                            }
                          `}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div
                        className={`
                          grid grid-cols-1 gap-8 border-t pt-6
                          ${TOKENS.BORDER.default}
                          md:grid-cols-2
                          ${TOKENS.ANIMATION.fadeInUp}
                        `}
                      >
                        <div className={`flex flex-col ${TOKENS.GAP.xl}`}>
                          <div className={`flex flex-col ${TOKENS.GAP.sm}`}>
                            <span className={eyebrow}>{content.projects.labels.problem}</span>
                            <p className={`text-sm leading-relaxed ${TOKENS.TEXT.secondary}`}>
                              {project.problem}
                            </p>
                          </div>
                          <div className={`flex flex-col ${TOKENS.GAP.sm}`}>
                            <span className={eyebrow}>{content.projects.labels.architecture}</span>
                            <p className={`text-sm leading-relaxed ${TOKENS.TEXT.secondary}`}>
                              {project.architecture}
                            </p>
                          </div>
                        </div>
                        <div className={`flex flex-col ${TOKENS.GAP.xl}`}>
                          <div className={`flex flex-col ${TOKENS.GAP.sm}`}>
                            <span className={eyebrow}>{content.projects.labels.outcomes}</span>
                            <ul className={`flex flex-col ${TOKENS.GAP.xs}`}>
                              {project.outcomes.map(item => (
                                <li
                                  key={`${project.id}-outcome-${item}`}
                                  className={`flex items-start gap-2.5 text-sm ${TOKENS.TEXT.secondary}`}
                                >
                                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#c95f3a]" aria-hidden="true" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className={`flex flex-col ${TOKENS.GAP.sm}`}>
                            <span className={eyebrow}>{content.projects.labels.keyArtifacts}</span>
                            <ul className={`flex flex-col ${TOKENS.GAP.xs}`}>
                              {project.artifacts.map(item => (
                                <li
                                  key={`${project.id}-artifact-${item}`}
                                  className={`flex items-start gap-2.5 text-sm ${TOKENS.TEXT.secondary}`}
                                >
                                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/40" aria-hidden="true" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        {/* ── Logo Stack ────────────────────────────────────────── */}
        <section className={sectionShell} aria-labelledby="stack-heading">
          <div className={`flex flex-col ${TOKENS.GAP['2xl']}`}>
            <div className="flex items-end justify-between gap-4">
              <div className={`flex flex-col ${TOKENS.GAP.md}`}>
                <span className={eyebrow}>{content.stack.eyebrow}</span>
                <h2 id="stack-heading" className={sectionHeading}>
                  {content.stack.title}
                </h2>
              </div>
              {content.stack.footnote ? (
                <span className={`${TOKENS.TEXT_SIZE.xs} font-mono ${TOKENS.TEXT.muted} shrink-0`}>
                  {content.stack.footnote}
                </span>
              ) : null}
            </div>

            {/* Marquee */}
            <div className="relative overflow-hidden border-t border-b border-border py-6">
              <div className="flex animate-marquee gap-6">
                {(['row-a', 'row-b'] as const).map(rowKey => (
                  <div
                    key={rowKey}
                    className="flex shrink-0 items-center gap-6 pr-6"
                    {...(rowKey === 'row-b' ? { 'aria-hidden': true, 'data-marquee-clone': '' } : {})}
                  >
                    {LOGO_ASSETS.map(logo => (
                      <a
                        key={`${rowKey}-${logo.name}`}
                        href={logo.href}
                        target="_blank"
                        rel="noreferrer"
                        title={logo.name}
                        tabIndex={rowKey === 'row-b' ? -1 : undefined}
                        className={`
                          group flex h-14 w-28 items-center justify-center
                          border rounded-none p-3
                          ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.base}
                          transition-colors duration-150 hover:bg-muted
                          ${TOKENS.INTERACTION.focusRing}
                        `}
                      >
                        <img
                          src={logo.sourceUrl}
                          alt={rowKey === 'row-b' ? '' : logo.name}
                          className={`
                            max-h-8 max-w-full object-contain
                            grayscale transition-opacity duration-150
                            ${isDarkMode ? 'opacity-60 group-hover:opacity-100' : 'opacity-50 group-hover:opacity-90'}
                          `}
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Production Background ─────────────────────────────── */}
        <section className={sectionShell} aria-labelledby="background-heading">
          <div className={`flex flex-col ${TOKENS.GAP['2xl']}`}>
            <div className={`flex flex-col ${TOKENS.GAP.md}`}>
              <span className={eyebrow}>{content.background.eyebrow}</span>
              <h2 id="background-heading" className={sectionHeading}>
                {content.background.title}
              </h2>
              <p className={`max-w-2xl ${bodyText}`}>
                {content.background.body}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-0 border-t border-b border-border md:grid-cols-3 md:divide-x divide-border divide-y md:divide-y-0">
              {content.background.items.map((item, i) => (
                <article
                  key={item.title}
                  className={`
                    flex flex-col ${TOKENS.GAP.sm} py-8
                    ${i === 0 ? 'md:pr-8' : i === 1 ? 'md:px-8' : 'md:pl-8'}
                  `}
                >
                  <div className={`flex flex-col ${TOKENS.GAP.xs}`}>
                    <h3 className={`text-base font-semibold tracking-tight ${TOKENS.TEXT.primary}`}>
                      {item.title}
                    </h3>
                    <p className={`${TOKENS.TEXT_SIZE.xs} font-mono ${TOKENS.TEXT.muted}`}>
                      {item.role} · {item.duration}
                    </p>
                  </div>
                  <p className={`text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
                    {item.details}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────────── */}
        <section className={sectionShell} aria-labelledby="cta-heading">
          <div className={`flex max-w-3xl flex-col ${TOKENS.GAP.xl}`}>
            <div className={`flex flex-col ${TOKENS.GAP.md}`}>
              <span className={eyebrow}>{content.cta.eyebrow}</span>
              <h2 id="cta-heading" className={sectionHeading}>
                {content.cta.title}
              </h2>
              <p className={bodyText}>
                {content.cta.body}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={onStartChat}
                className={`group relative w-full overflow-hidden sm:w-auto ${primaryCta}`}
              >
                <span className={TOKENS.INTERACTION.shimmer} />
                <span className="relative">{content.cta.openChat}</span>
                <svg
                  className="relative h-4 w-4 transition-transform duration-150 group-hover:translate-x-1"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => window.open(CONTACT_CONFIG.SCHEDULING.BOOKING_URL, '_blank')}
                className={`group relative w-full overflow-hidden sm:w-auto ${secondaryCta}`}
              >
                <span className={TOKENS.INTERACTION.shimmer} />
                <span className="relative">{content.cta.scheduleCall}</span>
              </button>
            </div>
          </div>
        </section>

      </main>
    </div>
  )
}

export default AboutPage
